import apiClient from './api';
import type { Notification, NotificationResponse } from '@/types/notifications';

export const notificationsService = {
  /**
   * Get all notifications for the current user
   */
  async getNotifications(params?: {
    page?: number;
    limit?: number;
    read?: boolean;
    type?: string;
    severity?: string;
  }): Promise<NotificationResponse> {
    const response = await apiClient.get<Notification[]>('/api/v1/notifications', {
      params,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to fetch notifications');
    }

    // The API returns: { success: true, data: Notification[], meta: {...} }
    // apiClient.get() returns: ApiResponse<Notification[]> where response.data is Notification[]
    const apiMeta = response.meta as { timestamp?: string; total?: number; unread?: number; page?: number; limit?: number; totalPages?: number } | undefined;
    return {
      success: response.success,
      data: response.data,
      meta: {
        timestamp: apiMeta?.timestamp ?? new Date().toISOString(),
        total: apiMeta?.total ?? response.data.length,
        unread: apiMeta?.unread ?? 0,
        page: apiMeta?.page ?? 1,
        limit: apiMeta?.limit ?? 50,
        totalPages: apiMeta?.totalPages ?? 1,
      },
    };
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const response = await apiClient.get<{ count: number }>('/api/v1/notifications/unread-count');

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to fetch unread count');
    }

    // The API returns: { success: true, data: { count: 1 }, meta: {...} }
    // apiClient.get() returns: ApiResponse<{ count: number }> where response.data is { count: number }
    return response.data.count;
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<Notification> {
    const response = await apiClient.patch<Notification>(
      `/api/v1/notifications/${id}/read`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to mark notification as read');
    }

    return response.data;
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    await apiClient.patch('/api/v1/notifications/read-all');
  },

  /**
   * Clear all notifications
   */
  async clearAll(): Promise<void> {
    await apiClient.delete('/api/v1/notifications/clear-all');
  },
};
